import { useCallback, useEffect, useState } from 'react';

// Hooks
import useFansUnitedApi from './useFansUnitedApi';

// Types
import {
  FansUnitedConfig,
  IFullTimeOneXTwo,
  IMarketSummaryResponse,
  IPredictionResponse,
  ISubscriberFn,
  LoadSmpData,
  OneXTwo,
} from '../types';
import { FansUnitedTokenProvider } from '@sportal365/smp-widget-utils';

interface IUsePredictorArgs {
  matchId?: string;
  fansUnitedMarket?: FansUnitedConfig['fansUnitedMarket'];
  predictionSign?: OneXTwo | null;
  fansUnitedApiKey?: FansUnitedConfig['fansUnitedApiKey'];
  fansUnitedClientId?: FansUnitedConfig['fansUnitedClientId'];
  fansUnitedEnvironment?: FansUnitedConfig['fansUnitedEnvironment'];
  fansUnitedIdSchema?: FansUnitedConfig['fansUnitedIdSchema'];
  fansUnitedGetAuthToken: LoadSmpData['getAuthToken'];
  fansUnitedSignOut: LoadSmpData['signOut'];
  subscribers: { [key: string]: ISubscriberFn[] };
}

export default function usePredictor({
  matchId,
  subscribers,
  predictionSign,
  fansUnitedApiKey,
  fansUnitedMarket,
  fansUnitedEnvironment,
  fansUnitedClientId,
  fansUnitedGetAuthToken,
  fansUnitedSignOut,
  fansUnitedIdSchema,
}: IUsePredictorArgs) {
  const authProvider = new FansUnitedTokenProvider({ getAuthToken: fansUnitedGetAuthToken, signOut: fansUnitedSignOut });

  const { $predictor } = useFansUnitedApi({
    apiKey: fansUnitedApiKey || '',
    clientId: fansUnitedClientId || '',
    idSchema: fansUnitedIdSchema,
    environment: fansUnitedEnvironment,
    authProvider,
  });

  const [prediction, setPrediction] = useState<IPredictionResponse>();
  const [summary, setSummary] = useState<IFullTimeOneXTwo>();

  const makePrediction = ({
    matchId,
    fansUnitedMarket,
    predictionSign,
  }: Pick<IUsePredictorArgs, 'matchId' | 'fansUnitedMarket' | 'predictionSign'>) => {
    if (predictionSign) {
      $predictor
        .makeFootballPrediction({ matchId, market: fansUnitedMarket, value: predictionSign })
        .then((res: IPredictionResponse) => {
          setPrediction(res);
          subscribers['onPredictionSuccess'].forEach((fn: ISubscriberFn) => fn(res));
        })
        .catch((error: unknown) => {
          subscribers['onPredictionError'].forEach((fn: ISubscriberFn) => fn(error));
        });
    }
  };

  const getMarketSummary = useCallback(() => {
    if (matchId) {
      $predictor
        .getMarketSummary({ matchId, market: fansUnitedMarket })
        .then((res: IMarketSummaryResponse) => {
          const homeWinVotes = res.fullTimeOneXTwo['1'];
          const awayWinVotes = res.fullTimeOneXTwo['2'];
          const drawVotes = res.fullTimeOneXTwo.x;
          const totalVotes = homeWinVotes + awayWinVotes + drawVotes;

          if (totalVotes > 0) {
            const summary = {
              1: (homeWinVotes / totalVotes) * 100,
              2: (awayWinVotes / totalVotes) * 100,
              x: (drawVotes / totalVotes) * 100,
            };
            setSummary(summary);
          }
        })
        .catch((error: never) => {
          console.error(error);
        });
    }
  }, [matchId, fansUnitedMarket, prediction, predictionSign]);

  useEffect(() => {
    const token = fansUnitedGetAuthToken();

    if (matchId && Boolean(token?.trim())) {
      $predictor
        .getMyPredictionsForMatches({ matchId })
        .then(({ data }: { data: IPredictionResponse[] }) => {
          setPrediction(data?.[0]);
        })
        .catch((error: never) => {
          console.error(error);
        });
    }
  }, [matchId, fansUnitedGetAuthToken]);

  return {
    summary,
    prediction,
    makePrediction,
    getMarketSummary,
  };
}
